import { useState, useEffect } from 'react';
import { normalizeFilterParams } from '../../utils/filterParams';
import s from './FilterBuilder.module.scss';

const INHERIT_COUNTRY_CODE = '__inherit_country_code__';

const FilterBuilder = ({
    filters = [],
    filterableFields = [],
    loadingFields = false,
    fetchColumnValues,
    currentDataset,
    onChange,
}) => {
    const [fieldValues, setFieldValues] = useState({});
    const [loadingValues, setLoadingValues] = useState({});
    const normalizedFilters = normalizeFilterParams(filters);

    // A field may be repeated across rows to build a multi-value filter:
    // several `field = …` rows match any of the values (IN), several
    // `field != …` rows exclude all of them (NOT IN). There is deliberately no
    // (field, operator) uniqueness rule — each row travels the wire as its own
    // repeated query-string key.
    const fieldTypeMap = new Map(filterableFields.map((f) => [f.id, f.type]));
    const canAddFilter = filterableFields.length > 0;

    // Clear cached field values and reload when dataset changes
    useEffect(() => {
        setFieldValues({});
        if (!fetchColumnValues || !currentDataset) return;
        normalizedFilters.forEach(async ({ field: fieldId }) => {
            const fieldType = fieldTypeMap.get(fieldId) || 'string';
            if (fieldType === 'date' || fieldType === 'datetime') return;
            setLoadingValues((prev) => ({ ...prev, [fieldId]: true }));
            try {
                const values = await fetchColumnValues(currentDataset, fieldId);
                setFieldValues((prev) => ({ ...prev, [fieldId]: values }));
            } finally {
                setLoadingValues((prev) => ({ ...prev, [fieldId]: false }));
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDataset]);

    // Get field type from filterableFields
    const getFieldType = (fieldId) => fieldTypeMap.get(fieldId) || 'string';

    // Fetch values when a field is selected
    const loadValuesForField = async (fieldId) => {
        if (!fetchColumnValues || !currentDataset || !fieldId) return;

        const fieldType = getFieldType(fieldId);
        // Don't fetch values for date fields
        if (fieldType === 'date' || fieldType === 'datetime') return;

        // Skip if already loaded
        if (fieldValues[fieldId]) return;

        setLoadingValues((prev) => ({ ...prev, [fieldId]: true }));
        try {
            const values = await fetchColumnValues(currentDataset, fieldId);
            setFieldValues((prev) => ({ ...prev, [fieldId]: values }));
        } finally {
            setLoadingValues((prev) => ({ ...prev, [fieldId]: false }));
        }
    };

    // Load values for newly added filter fields
    useEffect(() => {
        normalizedFilters.forEach((f) => {
            loadValuesForField(f.field);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleAddFilter = () => {
        if (!canAddFilter) return;
        const newField = filterableFields[0].id;
        onChange([
            ...normalizedFilters,
            { field: newField, op: 'eq', value: '' },
        ]);
        loadValuesForField(newField);
    };

    const handleRemoveFilter = (index) => {
        onChange(normalizedFilters.filter((_, i) => i !== index));
    };

    const handleFieldChange = (index, newField) => {
        const current = normalizedFilters[index];
        if (!current || current.field === newField) return;

        onChange(
            normalizedFilters.map((f, i) =>
                i === index ? { ...f, field: newField, value: '' } : f
            )
        );
        loadValuesForField(newField);
    };

    const handleOpChange = (index, newOp) => {
        const current = normalizedFilters[index];
        if (!current || (current.op || 'eq') === newOp) return;

        onChange(
            normalizedFilters.map((f, i) =>
                i === index ? { ...f, op: newOp } : f
            )
        );
    };

    const handleValueChange = (index, value) => {
        onChange(
            normalizedFilters.map((f, i) => (i === index ? { ...f, value } : f))
        );
    };

    const renderValueInput = (index, field, value) => {
        const fieldType = getFieldType(field);
        const isDateField = fieldType === 'date' || fieldType === 'datetime';
        const values = fieldValues[field] || [];
        const isLoading = loadingValues[field];
        const optionValues =
            value && !values.some((v) => String(v) === String(value))
                ? [value, ...values]
                : values;

        if (isDateField) {
            return (
                <input
                    type="date"
                    className={s.ValueInput}
                    value={value}
                    onChange={(e) => handleValueChange(index, e.target.value)}
                />
            );
        }

        return (
            <select
                className={s.ValueSelect}
                value={value}
                onChange={(e) => handleValueChange(index, e.target.value)}
                disabled={isLoading}>
                <option value="">Select value...</option>
                <option value={INHERIT_COUNTRY_CODE}>
                    Inherit country code
                </option>
                {optionValues.map((v) => (
                    <option key={v} value={v}>
                        {v}
                    </option>
                ))}
            </select>
        );
    };

    return (
        <div className={s.Root}>
            {normalizedFilters.length === 0 ? (
                <p className={s.EmptyText}>No filters applied</p>
            ) : (
                <div className={s.FilterList}>
                    {normalizedFilters.map(({ field, op, value }, index) => {
                        const currentOp = op || 'eq';
                        return (
                            <div
                                key={`${field}-${currentOp}-${index}`}
                                className={s.FilterRow}>
                                <select
                                    className={s.FieldSelect}
                                    value={field}
                                    onChange={(e) =>
                                        handleFieldChange(index, e.target.value)
                                    }
                                    disabled={loadingFields}>
                                    {filterableFields.map((f) => (
                                        <option key={f.id} value={f.id}>
                                            {f.id}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    className={s.OperatorSelect}
                                    value={currentOp}
                                    onChange={(e) =>
                                        handleOpChange(index, e.target.value)
                                    }
                                    disabled={loadingFields}>
                                    <option value="eq">equals</option>
                                    <option value="ne">not equal</option>
                                </select>
                                {renderValueInput(index, field, value)}
                                <button
                                    type="button"
                                    className={s.RemoveButton}
                                    onClick={() => handleRemoveFilter(index)}
                                    title="Remove filter">
                                    <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2">
                                        <path d="M18 6L6 18M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            <button
                type="button"
                className={s.AddButton}
                onClick={handleAddFilter}
                disabled={loadingFields || !canAddFilter}>
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                </svg>
                Add Filter
            </button>
        </div>
    );
};

export default FilterBuilder;
